import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { ComponentType } from "react";

import { useChatSurface } from "../surface.tsx";

/**
 * Registro de componentes de UI (seccion 9).
 *
 * El modelo no dibuja: nombra un componente de este registro y le pasa props.
 * Si el nombre no esta registrado se muestra un aviso claro en vez de fallar
 * en silencio -- un componente que el backend anuncia y el frontend no conoce
 * es un error de programacion que debe verse.
 *
 * Un componente puede registrar además una versión COMPACTA, que es la que se
 * dibuja en la ventana pequeña de Work. Vive aquí y no en un `switch` fuera
 * porque el registro es el único sitio donde se declara un componente: si
 * hubiera que añadirlo en dos sitios, el segundo se olvidaría.
 */
export type UiComponentProps = Record<string, unknown>;

type UiComponent = ComponentType<{ props: UiComponentProps }>;

interface Registered {
  readonly full: UiComponent;
  /** Sin versión compacta, se dibuja la de siempre: es que ya cabe. */
  readonly compact: UiComponent;
}

const registry = new Map<string, Registered>();

export const registerUiComponent = (
  name: string,
  component: UiComponent,
  compact?: UiComponent,
): void => {
  registry.set(name, { full: component, compact: compact ?? component });
};

export function UiComponent({
  render,
  props,
}: {
  render: string;
  props: UiComponentProps;
}) {
  const { density } = useChatSurface();
  const registered = registry.get(render);

  if (registered === undefined) {
    return (
      <Alert className="border-warning">
        <AlertTitle>Componente no registrado: {render}</AlertTitle>
        <AlertDescription>
          El backend pidió dibujar <code>{render}</code>, pero el frontend no lo
          conoce. Falta registrarlo en <code>chat/components/registry.tsx</code>
          .
        </AlertDescription>
      </Alert>
    );
  }

  const Component =
    density === "compact" ? registered.compact : registered.full;

  return <Component props={props} />;
}
